<script>
import { AVATAR_DA_CONVERSA } from '@modules/aawz-intelligence-v2/components/avatar-da-conversa/copy'
import { urlDoClipe } from '@modules/aawz-intelligence-v2/components/boas-vindas-falada/provedor-de-clipe'

// ★ O AVATAR DA CONVERSA — quem está do outro lado depois da boas-vindas
// (spec-009, D-009-5: *"o primeiro contato é com o AVATAR, não com o modo
// voz"*).
//
// Ele é BURRO por inteiro: recebe `falando` e a mídia, desenha o rosto e o
// estado, e emite `encerrar`. Quem sabe que a conversa existe, quem manda o
// turno e quem toca o áudio é o host (`view-chat-conversa-com-avatar.mixin.js`
// + a fila de `fala-da-ia.js`) — este componente não abre sessão, não pede
// microfone e não sintetiza nada.
//
// ★ **O rosto é OPCIONAL, por construção.** É a mesma régua do player da
// boas-vindas: o clipe do vendor é gerado no BACKEND (a chave nunca vai ao
// browser) e leva minutos, então ninguém espera por ele. Sem clipe e sem
// retrato, fica o selo com a marca da IA — e a conversa falada continua
// inteira, porque o que importa é a FALA, não o vídeo.
//
// ★ **O estado é derivado, nunca uma flag própria.** `falando` chega do host,
// que o lê de `motorDeVoz.estadoDaFala` — o mesmo campo que a fila de fala já
// mantém em todos os desfechos (fim natural, interrupção, falha do áudio).
// Uma flag local aqui seria uma segunda verdade, e ela divergiria no primeiro
// erro de áudio.
export default {
  name: 'AvatarDaConversa',
  props: {
    // O avatar está falando AGORA. É o que troca o rótulo e acende o anel.
    falando: {
      type: Boolean,
      default: false,
    },
    // Clipe de fundo, quando o host já tem a URL. Opcional.
    clipe: {
      type: String,
      default: '',
    },
    // Retrato estático — usado quando não há clipe. Também opcional.
    retrato: {
      type: String,
      default: '',
    },
    // O provedor (`boas-vindas-falada/provedor-de-clipe.js`), quando o host
    // prefere delegar a busca. Resolvido SEM ESPERA, como no player.
    provedorDeClipe: {
      type: Object,
      default: null,
    },
    // ★ AVATAR AO VIVO (spec-009): as faixas que voltam da sala do provedor.
    // Quando existem, elas VENCEM o clipe e o retrato — é o rosto de verdade
    // falando o que a IA disse, em vez de um laço de vídeo genérico.
    //
    // ⚠️ A de ÁUDIO não é opcional quando a de vídeo existe: com o avatar
    // ligado, a fila de fala deixou de tocar o `<audio>` local, e é esta
    // faixa que faz som. Sem ela o avatar mexe a boca em silêncio.
    faixaDeVideo: {
      type: Object,
      default: null,
    },
    faixaDeAudio: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      copy: AVATAR_DA_CONVERSA,
      // A URL que o provedor entregou depois de o componente já estar na tela.
      // Vazia em tudo que não é clipe pronto: gerando, indisponível e ausente
      // caem no mesmo lugar.
      clipeResolvido: '',
    }
  },
  computed: {
    // O rosto AO VIVO vence tudo: clipe e retrato são substitutos dele.
    temAoVivo() {
      return Boolean(this.faixaDeVideo)
    },
    // O que o host passou vence o que o provedor buscou — mesma precedência do
    // player da boas-vindas.
    clipeEfetivo() {
      return this.clipe || this.clipeResolvido
    },
    temClipe() {
      return !this.temAoVivo && Boolean(this.clipeEfetivo)
    },
    temRetrato() {
      return !this.temAoVivo && !this.temClipe && Boolean(this.retrato)
    },
    // Sem rosto ao vivo, sem clipe e sem retrato: o selo da IA ocupa o lugar.
    temSelo() {
      return !this.temAoVivo && !this.temClipe && !this.temRetrato
    },
    rotuloDoEstado() {
      return this.falando ? this.copy.FALANDO : this.copy.AGUARDANDO
    },
  },
  watch: {
    // As faixas chegam DEPOIS da montagem (a sala é aberta em paralelo) e
    // podem trocar se a sessão reconectar — por isso watcher.
    //
    // ⚠️ SEM `immediate`, e o `mounted` abaixo anexa DIRETO, sem `$nextTick`:
    // no `mounted` o DOM já existe, e dois `$nextTick` a mais na montagem
    // deslocam a contagem de ticks de quem monta o componente — foi o
    // vermelho de `clipe PRONTO entra depois de a tela já estar montada`,
    // um teste que não tem nada a ver com avatar ao vivo. O `$nextTick` só
    // faz falta no watcher, onde o `<video>` ainda não existe no instante
    // em que a faixa chega.
    faixaDeVideo: 'anexarVideoQuandoRenderizar',
    faixaDeAudio: 'anexarAudioQuandoRenderizar',
  },
  mounted() {
    // Sem `await`: o rosto é enfeite, e enfeite não segura a conversa.
    this.resolverClipe()
    this.anexarFaixa(this.faixaDeVideo, this.$refs.video)
    this.anexarFaixa(this.faixaDeAudio, this.$refs.audio)
  },
  beforeDestroy() {
    this._destruido = true
    // Desanexar é obrigatório: uma faixa que segue presa a um elemento
    // destruído mantém o decodificador vivo e o áudio tocando fora da tela.
    this.desanexar(this.faixaDeVideo, this.$refs.video)
    this.desanexar(this.faixaDeAudio, this.$refs.audio)
  },
  methods: {
    // `obter()` nunca rejeita (a degradação é o contrato dele), então não há
    // `catch` a fazer: gerando, indisponível e ausente devolvem URL vazia e a
    // tela segue com o retrato ou com o selo.
    // `attach(el)` é do SDK do provedor de vídeo (LiveKit). Envolto em
    // try/catch porque uma faixa já encerrada estoura ali — e o desfecho
    // certo é a tela seguir sem rosto, nunca a conversa cair.
    anexarFaixa(faixa, elemento) {
      if (!faixa || !elemento || typeof faixa.attach !== 'function') return
      try {
        faixa.attach(elemento)
      } catch (e) {
        // Sem rosto (ou sem som) é um estado previsto; o turno continua.
      }
    },
    desanexar(faixa, elemento) {
      if (!faixa || !elemento || typeof faixa.detach !== 'function') return
      try {
        faixa.detach(elemento)
      } catch (e) {
        // idem
      }
    },
    // `$nextTick`: o `<video>` só existe depois de `temAoVivo` virar `true`,
    // e o watcher roda ANTES do render que o cria.
    anexarVideoQuandoRenderizar() {
      this.$nextTick(() => this.anexarFaixa(this.faixaDeVideo, this.$refs.video))
    },
    anexarAudioQuandoRenderizar() {
      this.$nextTick(() => this.anexarFaixa(this.faixaDeAudio, this.$refs.audio))
    },
    async resolverClipe() {
      if (this.clipe || !this.provedorDeClipe) return
      const resultado = await this.provedorDeClipe.obter()
      if (this._destruido) return
      this.clipeResolvido = urlDoClipe(resultado)
    },
  },
}
</script>

<template>
  <section
    class="avatar-da-conversa az-card-bordered d-flex align-center az-gap pa-4"
    :aria-label="copy.ARIA"
    data-test="avatar-da-conversa"
  >
    <!-- Clipe OU retrato OU selo: os três são estados previstos. `muted` +
         `playsinline` porque o som é do TTS — o clipe é imagem em movimento,
         nunca fonte de áudio. -->
    <!-- ★ O rosto AO VIVO. Maior que o clipe de propósito: ele é o objeto do
         teste — num círculo de 48px ninguém julga se a experiência é boa.
         `playsinline` para o iOS não abrir em tela cheia; o som vem da faixa
         de áudio abaixo, não daqui. -->
    <video
      v-if="temAoVivo"
      ref="video"
      class="avatar-da-conversa__ao-vivo"
      :class="{ 'avatar-da-conversa__ao-vivo--falando': falando }"
      autoplay
      muted
      playsinline
      data-test="avatar-da-conversa-ao-vivo"
    />
    <video
      v-else-if="temClipe"
      class="avatar-da-conversa__midia"
      :class="{ 'avatar-da-conversa__midia--falando': falando }"
      :src="clipeEfetivo"
      autoplay
      muted
      loop
      playsinline
      data-test="avatar-da-conversa-clipe"
    />
    <img
      v-else-if="temRetrato"
      class="avatar-da-conversa__midia"
      :class="{ 'avatar-da-conversa__midia--falando': falando }"
      :src="retrato"
      :alt="copy.ARIA_RETRATO"
      data-test="avatar-da-conversa-retrato"
    />
    <span
      v-else
      class="avatar-da-conversa__midia avatar-da-conversa__selo d-flex align-center justify-center"
      :class="{ 'avatar-da-conversa__midia--falando': falando }"
      data-test="avatar-da-conversa-selo"
    >
      <icon-svg name="ai-sparkle" :size="20" color="var(--primary)" />
    </span>

    <!-- `role="status"`: a troca de vez é anunciada sem roubar o foco de quem
         está digitando. -->
    <p
      class="avatar-da-conversa__estado az-text-primary az-size-14 mb-0"
      role="status"
      :data-falando="falando ? 'true' : 'false'"
      data-test="avatar-da-conversa-estado"
    >
      {{ rotuloDoEstado }}
    </p>

    <BaseButton
      underlined
      fit-content
      class="ml-auto"
      data-test="avatar-da-conversa-encerrar"
      @click="$emit('encerrar')"
    >
      {{ copy.ENCERRAR }}
    </BaseButton>

    <!-- A faixa de áudio da sala: é ela que faz som enquanto o avatar está
         ligado (a fila de fala não toca mais o `<audio>` local). Sem
         controles e sem tamanho — não é um player, é o alto-falante.
         ⚠️ FORA da cadeia `v-if`/`v-else-if` do rosto, e no fim do bloco:
         um `v-if` no meio da cadeia ORFANIZA o `v-else-if` seguinte, e o
         clipe simplesmente deixa de renderizar (medido — foi o vermelho de
         `clipe PRONTO entra depois de a tela já estar montada`). -->
    <audio
      v-if="temAoVivo"
      ref="audio"
      autoplay
      data-test="avatar-da-conversa-audio"
    />
  </section>
</template>

<style lang="sass" scoped>
.avatar-da-conversa
  &__midia
    width: 48px
    height: 48px
    flex: 0 0 auto
    object-fit: cover
    border-radius: 50%
    // O anel existe nos dois estados para a troca não mexer no layout — o que
    // muda é a COR dele, nunca a espessura.
    border: 2px solid var(--neutral-200)
    transition: border-color var(--motion-duration-default) var(--motion-easing-standard)

    &--falando
      border-color: var(--primary)
      // O pulso é do ANEL (`box-shadow`), que é composto pela GPU — nada de
      // animar `width`/`border-width`, que refluiriam a linha inteira a cada
      // quadro.
      animation: avatar-da-conversa-pulso 1.6s ease-in-out infinite

  &__ao-vivo
    // 160px: grande o bastante para julgar expressão e sincronia labial —
    // que é o que este teste precisa responder —, e pequeno o bastante para
    // caber no rodapé sem roubar a conversa. O retrato circular de 48px
    // continua para clipe/selo, que são enfeite; este é o objeto do teste.
    width: 160px
    height: 160px
    flex: 0 0 auto
    object-fit: cover
    border-radius: var(--radius-lg)
    background-color: var(--neutral-900)
    border: 2px solid var(--neutral-200)
    transition: border-color var(--motion-duration-default) var(--motion-easing-standard)

    &--falando
      border-color: var(--primary)

  &__selo
    background-color: var(--neutral-50)

@keyframes avatar-da-conversa-pulso
  0%
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--primary) 35%, transparent)
  70%
    box-shadow: 0 0 0 8px transparent
  100%
    box-shadow: 0 0 0 0 transparent

@media (prefers-reduced-motion: reduce)
  .avatar-da-conversa__midia--falando
    animation: none
</style>
